import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  exhaustMap,
  map,
  mergeMap,
  switchMap,
  take,
  catchError,
} from 'rxjs/operators';
import {
  Horse,
  HorseActionSuccessTypeEnum,
  HorseClass,
} from '@sleipai/sleip-models';
import { HorseService } from '../../horses-store/services/horse.service';
import * as HorsesActions from '../actions/horses.actions';
import { UserService } from '../../user-store/services/user.service';
import { UserActions } from '../../user-store';
import { RecordingActions } from '../../recordings-store';
import { User } from '@sleipai/sleip-models';
import { concat, defer, of } from 'rxjs';
import { Update } from '@ngrx/entity';

@Injectable()
export class HorsesEffects {
  loadHorses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.loadHorses),
      switchMap(({ uid }) => {
        const ownerStream = this.horseService.getHorseByUid(uid);
        return ownerStream.pipe(
          map((horses) => {
            return HorsesActions.addHorses({ horses });
          })
        );
      })
    );
  });

  loadConsumerHorses = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.loadConsumerHorses),
      switchMap(({ uid }) => {
        const consumerStream = this.horseService.getConsumerHorseByUid(uid);
        return consumerStream.pipe(
          map((horses) => {
            return HorsesActions.addHorses({ horses });
          })
        );
      })
    );
  });

  loadSingleHorseById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.loadSinglehorse),
      mergeMap(({ horseId: id }) => {
        const singleHorse = this.horseService.getSingleHorse(id);
        return singleHorse.pipe(
          map((horse) => {
            return HorsesActions.addSingleHorse({ horse });
          })
        );
      })
    );
  });

  loadCombinedLongeingsOnSingleHorseLoad$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.loadSinglehorse),
      map(({ horseId }) =>
        RecordingActions.loadCombinedLongeingsForHorse({ horseId })
      )
    );
  });

  loadAdminHorses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.loadAdminHorses),
      exhaustMap(() => {
        return this.horseService.getAllHorses().pipe(
          map((horses) => {
            return HorsesActions.addHorses({ horses });
          })
        );
      })
    );
  });

  loadSharedHorses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.loadSharedHorses),
      mergeMap(({ groupId }) => {
        return this.horseService.getHorsesWithGroupId(groupId).pipe(
          mergeMap((horses: Horse[]) => {
            return this.userService.getUersByGroupId(groupId).pipe(
              take(2),
              switchMap((users: User[]) => {
                return [
                  HorsesActions.addHorses({ horses }),
                  UserActions.addUsers({ users }),
                ];
              })
            );
          })
        );
      })
    );
  });

  loadHorsesAssociatedWithUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.loadHorsesAssociatedUser),
      exhaustMap(({ uid }) => {
        return this.horseService.getConsumerHorseByUid(uid).pipe(
          map((horses: Horse[]) => {
            return HorsesActions.addHorses({ horses });
          })
        );
      })
    );
  });

  loadHorsesByHorseIds$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.loadHorsesByIds),
      map((action) => action),
      mergeMap(({ horseIds }) => {
        return this.horseService.getHorsesBasedOnIds(horseIds).pipe(
          map((horses: Horse[]) => {
            return HorsesActions.addHorses({ horses });
          })
        );
      })
    );
  });

  loadHorsesFromAlgoliaSidb$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.loadHorsesFromAlgoliaSidb),
      mergeMap(({ queryConfig, updateTotalCount }) => {
        return this.horseService.searchHorsesInAlgoliaSidb(queryConfig).pipe(
          map((data) => {
            return HorsesActions.loadHorsesFromAlgoliaSidbSuccess({
              data,
              updateTotalCount,
            });
          }),
          catchError((error) => {
            console.log(error);
            return [
              HorsesActions.loadHorsesFromAlgoliaSidbFailure({
                error: error?.message || error,
              }),
            ];
          })
        );
      })
    );
  });
  
  loadHorsesForInfiniteScrollList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HorsesActions.loadHorsesForInfiniteScrollList),
      switchMap(({ reset = false, queryConfig }) => {
        return this.horseService
          .fetchHorsesForInfiniteScrollInDashBoard(reset, queryConfig)
          .pipe(
            map((result) => {
              return HorsesActions.loadHorsesForInfiniteScrollListSuccess({
                horses: result.horses,
                hasMore: result.hasMore,
              });
            }),
            catchError((error) => {
              console.error(error);
              return of(
                HorsesActions.loadHorsesForInfiniteScrollListFailure({
                  error:
                    error.message ||
                    'An error occurred while loading horses',
                })
              );
            })
          );
      })
    )
  );

  createHorseWithTags$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.createHorseWithTags),
      map((action) => action),
      switchMap(({ data }) => {
        return this.horseService.createHorseWithTags(data).pipe(
          switchMap((data) => {
            if (typeof data === 'object') {
              const newHorse = new HorseClass({
                ...data.horse,
                id: data.horseId,
                horseId: data.horseId,
              });
              return [
                HorsesActions.saveHorseWithTagsSuccess({
                  data: newHorse,
                  horseActionSuccessType:
                    HorseActionSuccessTypeEnum.HORSE_ADDED,
                }),
                HorsesActions.resetHorseSavedFlag(),
              ];
            } else {
              const errorResponse = {
                message: 'An error occurred, Adding new Horse Failed',
              };
              return [
                HorsesActions.saveHorseWithTagsFailure({ error: errorResponse }),
                HorsesActions.saveHorseWithTagsFailure({ error: null }),
              ];
            }
          }),
          catchError((error) => {
            const errorResponse = {
              ...error,
              message: 'An error occurred, Adding new Horse Failed',
            };
            return [
              HorsesActions.saveHorseWithTagsFailure({ error: errorResponse }),
              HorsesActions.saveHorseWithTagsFailure({ error: null }),
            ];
          })
        );
      })
    );
  });

  updateHorseWithTags$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.updateHorseWithTags),
      map((action) => action),
      switchMap(({ data }) => {
        return this.horseService.updateHorseWithTags(data).pipe(
          switchMap((data) => {
            const updatedHorse = new HorseClass(data);
            return [
              HorsesActions.saveHorseWithTagsSuccess({
                data: updatedHorse,
                horseActionSuccessType:
                  HorseActionSuccessTypeEnum.HORSE_UPDATED,
              }),
              HorsesActions.resetHorseSavedFlag(),
            ];
          }),
          catchError((error) => {
            const errorResponse = {
              ...error,
              message: 'An error occurred, Updating Horse Failed',
            };
            return [
              HorsesActions.saveHorseWithTagsFailure({ error: errorResponse }),
              HorsesActions.saveHorseWithTagsFailure({ error: null }),
            ];
          })
        );
      })
    );
  });

  softDeleteHorse$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.removeHorse),
      switchMap(({ horseToDelete }) => {
        return this.horseService.removeHorse(horseToDelete).pipe(
          switchMap((response) => {
            if (response && horseToDelete.id) {
              return [
                HorsesActions.removeHorseSuccess({
                  horseId: horseToDelete.id,
                  horseActionSuccessType:
                    HorseActionSuccessTypeEnum.HORSE_REMOVED,
                }),
                HorsesActions.resetRemoveHorseFlag(),
              ];
            } else {
              return [
                HorsesActions.horseDetailsActionFailure({
                  error: 'Something went wrong while removing the horse',
                }),
              ];
            }
          }),
          catchError((error) =>
            of(HorsesActions.horseDetailsActionFailure({ error }))
          )
        );
      })
    );
  });

  updateHorseName$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.updateHorseName),
      switchMap(({ horseToUpdate, name }) =>
        defer(() =>
          this.horseService.updateHorseName(horseToUpdate.id as string, name)
        ).pipe(
          switchMap(() => {
            const updatePayload: Update<Horse> = {
              id: horseToUpdate.id as string,
              changes: { ...horseToUpdate, name },
            };

            return [
              HorsesActions.horseUpdateSuccess({
                update: updatePayload,
                horseActionSuccessType:
                  HorseActionSuccessTypeEnum.HORSE_NAME_UPDATED,
              }),
              HorsesActions.resetHorseSavedFlag(),
            ];
          }),
          catchError(() =>
            of(
              HorsesActions.horseDetailsActionFailure({
                error: 'Something went wrong while updating the horse name',
              })
            )
          )
        )
      )
    );
  });

  cloneHorse$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.cloneHorse),
      switchMap(({ cloneHorse }) =>
        defer(() => this.horseService.cloneHorse(cloneHorse)).pipe(
          switchMap(() => {
            return [
              HorsesActions.cloneHorseSuccess({
                horseActionSuccessType: HorseActionSuccessTypeEnum.HORSE_CLONED,
              }),
              HorsesActions.resetHorseSavedFlag(),
            ];
          }),
          catchError(() =>
            of(
              HorsesActions.horseDetailsActionFailure({
                error: 'Something went wrong while cloning the horse',
              })
            )
          )
        )
      )
    );
  });

  batchStopShareHorse$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.batchStopShareHorse),
      switchMap(({ uids, horseId }) =>
        defer(() => this.horseService.batchStopShareHorse(uids, horseId)).pipe(
          switchMap((response) => {
            return [
              HorsesActions.batchStopShareHorseSuccess({
                failedIds: response?.failedIds ?? [],
                horseActionSuccessType:
                  HorseActionSuccessTypeEnum.HORSE_BATCH_STOP_SHARE,
              }),
              HorsesActions.resetHorseSavedFlag(),
            ];
          }),
          catchError((error) =>
            of(
              HorsesActions.batchStopShareHorseFailure({
                error: {
                  ...error,
                  message: 'An error occurred while removing horse access',
                },
              })
            )
          )
        )
      )
    );
  });

  mergeHorses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.mergeHorses),
      switchMap(({ horseIdToMerge, horseIdToMergeTo }) =>
        defer(() =>
          this.horseService.mergeHorses(horseIdToMerge, horseIdToMergeTo)
        ).pipe(
          switchMap(() => [
            HorsesActions.mergeHorsesSuccess({
              horseIdToMerge,
              horseActionSuccessType: HorseActionSuccessTypeEnum.HORSE_MERGED,
            }),
            HorsesActions.resetHorseSavedFlag(),
          ]),
          catchError((error) =>
            of(HorsesActions.horseDetailsActionFailure({ error }))
          )
        )
      )
    );
  });

  batchCloneHorses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.batchCloneHorses),
      switchMap(({ data }) =>
        defer(() => this.horseService.batchCloneHorses(data)).pipe(
          switchMap((response) => {
            const clonedHorseIds = response?.clonedHorseIds ?? [];
            const failedIds = response?.failedIds ?? [];
            const immediateActions = [
              HorsesActions.batchCloneHorsesSuccess({
                failedIds,
                horseActionSuccessType: HorseActionSuccessTypeEnum.HORSES_BATCH_CLONED,
              }),
              HorsesActions.resetHorseSavedFlag(),
            ];

            if (!clonedHorseIds.length) {
              return of(...immediateActions);
            }

            return concat(
              of(...immediateActions),
              this.horseService.getHorsesBasedOnIds(clonedHorseIds).pipe(
                take(1),
                map((horses) => HorsesActions.addHorses({ horses }))
              )
            );
          }),
          catchError((error) =>
            of(
              HorsesActions.batchCloneHorsesFailure({
                error: {
                  ...error,
                  message: error?.message ?? 'An error occurred, Batch Clone Horses Failed',
                },
              })
            )
          )
        )
      )
    );
  });

  batchDeleteHorses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesActions.batchDeleteHorses),
      switchMap(({ ids }) =>
        defer(() => this.horseService.batchDeleteHorses(ids)).pipe(
          switchMap((response) => [
            HorsesActions.batchDeleteHorsesSuccess({
              ids,
              failedIds: response?.failedIds ?? [],
              horseActionSuccessType: HorseActionSuccessTypeEnum.HORSES_BATCH_DELETED,
            }),
            HorsesActions.resetHorseSavedFlag(),
          ]),
          catchError((error) =>
            of(
              HorsesActions.batchDeleteHorsesFailure({
                error: {
                  ...error,
                  message: error?.message ?? 'An error occurred, Batch Delete Horses Failed',
                },
              })
            )
          )
        )
      )
    );
  });

  constructor(
    private actions$: Actions,
    private horseService: HorseService,
    private userService: UserService
  ) {}
}
